"use client"

import { useState, useEffect } from "react"
import { Card, CardContent } from "@/components/ui/card"
import { Tag, ArrowRight } from "lucide-react"
import Link from "next/link"
import Image from "next/image"

function getTimeLeft() {
    const deadline = new Date("2026-03-20T23:59:00")
    const now = new Date()
    const diff = deadline.getTime() - now.getTime()
    if (diff <= 0) return { hours: 0, minutes: 0, seconds: 0, expired: true }
    const totalSeconds = Math.floor(diff / 1000)
    return {
        hours: Math.floor(totalSeconds / 3600),
        minutes: Math.floor((totalSeconds % 3600) / 60),
        seconds: totalSeconds % 60,
        expired: false,
    }
}

function pad(n: number) {
    return String(n).padStart(2, "0")
}


export function CouponCard() {
    const [timeLeft, setTimeLeft] = useState(getTimeLeft)
    const [mounted, setMounted] = useState(false)

    useEffect(() => { setMounted(true) }, [])
    useEffect(() => {
        const id = setInterval(() => setTimeLeft(getTimeLeft()), 1000)
        return () => clearInterval(id)
    }, [])

    if (timeLeft.expired) return null

    return (
        <div className="w-full">
            <h3 className="text-xl font-bold mb-4 text-foreground">
                Limited Offer
            </h3>

            <Link
                href="https://dsatschool.com/courses/may-sat-foundation-to-advanced-program"
                target="_blank"
                rel="noopener noreferrer"
                className="block"
            >
                <Card className="w-full border-0 overflow-hidden relative shadow-md group text-primary-foreground cursor-pointer hover:shadow-xl transition-shadow duration-300 bg-gradient-to-br from-primary to-secondary">
                    <CardContent className="p-6 flex items-center justify-between z-10 relative">
                        <div className="flex-1 min-w-0">
                            {/* Header row */}
                            <div className="flex items-center gap-3 mb-3">
                                <div className="bg-white/10 backdrop-blur-sm p-2 rounded-xl shadow-sm border border-white/10">
                                    <Tag className="w-5 h-5" />
                                </div>
                                <span className="text-2xl font-bold tracking-tight">GIFT26</span>
                            </div>

                            {/* Description */}
                            <p className="text-sm font-medium opacity-90 mb-4 leading-snug">
                                Flat 40% off on{" "}
                                <span className="underline underline-offset-2 font-bold">
                                    DSAT Foundation to Advanced Program
                                </span>
                            </p>

                            {/* Countdown */}
                            <div className="flex items-end gap-2">
                                <div className="flex items-center gap-1.5">
                                    <span className="text-4xl font-extrabold tracking-tight drop-shadow-sm tabular-nums">
                                        {pad(timeLeft.hours)}
                                    </span>
                                    <span className="text-sm font-medium opacity-75 mb-0.5">hrs</span>
                                    <span className="text-3xl font-bold opacity-60 mx-0.5">:</span>
                                    <span className="text-4xl font-extrabold tracking-tight drop-shadow-sm tabular-nums">
                                        {pad(timeLeft.minutes)}
                                    </span>
                                    <span className="text-sm font-medium opacity-75 mb-0.5">min</span>
                                    <span className="text-3xl font-bold opacity-60 mx-0.5">:</span>
                                    <span className="text-4xl font-extrabold tracking-tight drop-shadow-sm tabular-nums">
                                        {pad(timeLeft.seconds)}
                                    </span>
                                    <span className="text-sm font-medium opacity-75 mb-0.5">sec</span>
                                </div>
                            </div>
                            <p className="text-xs font-semibold uppercase tracking-widest opacity-70 mt-1">
                                remaining — expires Mar 20
                            </p>
                        </div>

                        {/* Arrow */}
                        <div className="flex flex-col items-center gap-3 ml-4 shrink-0">
                            <div className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-md border border-white/10 flex items-center justify-center transition-colors shadow-sm group-hover:bg-white group-hover:text-primary">
                                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                            </div>
                        </div>
                    </CardContent>

                    {/* Decorative background blobs */}
                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-bl-[100px] -z-10 group-hover:scale-110 transition-transform duration-500" />
                    <div className="absolute -bottom-8 -left-8 w-24 h-24 bg-white/5 rounded-tr-[80px] -z-10 group-hover:scale-110 transition-transform duration-700 delay-75" />
                    {/* Eid background image */}
                    <div className="absolute -right-4 -bottom-6 w-56 h-56 opacity-50 pointer-events-none select-none group-hover:opacity-65 transition-opacity duration-500">
                        <Image src="/eid.png" alt="" fill className="object-contain drop-shadow-lg" />
                    </div>
                </Card>
            </Link>
        </div>
    )
}
